import { useCallback, useEffect, useState } from 'react';
import { Copy, Key, Plus, Trash } from '@phosphor-icons/react';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';
import { useT } from '../i18n/I18nContext';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function ProjectApiKeysPanel({ project }) {
  const t = useT();
  const { user } = useAuth();
  const isOwner = user && project.owner_id === user.id;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/api-keys`);
      setKeys(data);
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.apiKeys.loadFailedToast')));
    } finally {
      setLoading(false);
    }
  }, [isOwner, project.id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const createKey = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/api-keys`, { name: name.trim() });
      setCreatedKey(data);
      setName('');
      await load();
      toast.success(t('settings.apiKeys.createdToast'));
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.apiKeys.createFailedToast')));
    } finally {
      setCreating(false);
    }
  };

  const copyKey = async () => {
    if (!createdKey?.api_key) return;
    try {
      await navigator.clipboard.writeText(createdKey.api_key);
      toast.success(t('settings.apiKeys.copiedToast'));
    } catch {
      toast.error(t('settings.apiKeys.copyFailedToast'));
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await api.delete(`/projects/${project.id}/api-keys/${revokeTarget.id}`);
      toast.success(t('settings.apiKeys.revokedToast'));
      setRevokeTarget(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('settings.apiKeys.revokeFailedToast')));
      throw err;
    } finally {
      setRevoking(false);
    }
  };

  if (!isOwner) {
    return (
      <p className="text-xs text-zinc-500">{t('settings.apiKeys.ownerOnlyHint')}</p>
    );
  }

  return (
    <>
      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title={t('settings.apiKeys.revokeConfirmTitle')}
        description={t('settings.apiKeys.revokeConfirmDescription', { name: revokeTarget?.name || '' })}
        confirmLabel={t('settings.apiKeys.revokeConfirmBtn')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={revoking}
        onConfirm={confirmRevoke}
      />
      <Dialog open={!!createdKey} onOpenChange={(open) => !open && setCreatedKey(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight">
              {t('settings.apiKeys.createdDialogTitle')}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {t('settings.apiKeys.createdDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">{t('settings.apiKeys.secretLabel')}</Label>
            <div className="flex gap-2">
              <Input
                readOnly
                value={createdKey?.api_key || ''}
                className="rounded-sm font-mono text-xs"
              />
              <Button type="button" variant="outline" className="rounded-sm shrink-0" onClick={copyKey}>
                <Copy size={14} />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" className="rounded-sm" onClick={() => setCreatedKey(null)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5">
            <Key size={12} /> {t('settings.apiKeys.title')}
          </p>
          <p className="text-xs text-zinc-500 mt-1">{t('settings.apiKeys.description')}</p>
        </div>

        <form onSubmit={createKey} className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wider font-semibold">{t('settings.apiKeys.nameLabel')}</Label>
            <Input
              data-testid="api-key-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.apiKeys.namePlaceholder')}
              className="rounded-sm mt-1"
              maxLength={64}
            />
          </div>
          <Button
            type="submit"
            data-testid="api-key-create-btn"
            disabled={creating || !name.trim()}
            className="rounded-sm bg-black text-white hover:bg-zinc-800 shrink-0"
          >
            <Plus size={14} className="mr-1" />
            {creating ? t('settings.apiKeys.creatingBtn') : t('settings.apiKeys.createBtn')}
          </Button>
        </form>

        {loading ? (
          <p className="text-xs text-zinc-500">{t('common.loading')}</p>
        ) : keys.length === 0 ? (
          <p className="text-xs text-zinc-500">{t('settings.apiKeys.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {keys.map((k) => (
              <li
                key={k.id}
                className="flex items-center justify-between gap-3 rounded-sm border border-zinc-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{k.name}</p>
                  <p className="text-[10px] font-mono text-zinc-500">
                    {k.key_prefix}… · {t('settings.apiKeys.createdAt', { when: formatWhen(k.created_at) })}
                    {k.last_used_at ? ` · ${t('settings.apiKeys.lastUsedAt', { when: formatWhen(k.last_used_at) })}` : ''}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700 shrink-0"
                  onClick={() => setRevokeTarget(k)}
                >
                  <Trash size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <pre className="text-[10px] font-mono text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-sm p-2 overflow-x-auto whitespace-pre-wrap">
          {t('settings.apiKeys.example', { projectId: project.id })}
        </pre>
      </div>
    </>
  );
}
